/**
 * Connor — the AAS pelican, and the hub's appetite assistant.
 *
 * Named for the COO's son. Members just meet a pelican called Connor; how much
 * more than that is ever said is the family's call, not the product's.
 *
 * Both poses are real illustrations, not drawings assembled here. Roughly ten
 * rounds of hand-authored SVG produced, in order, a penguin, a rooster and a
 * duck — writing bezier coordinates without seeing the curve as you pull it has
 * a ceiling well below what an illustrator reaches in one pass. This file only
 * places the art.
 *
 * The images are prepared by the scripts in `apps/web/scripts/`, with the source
 * files checked in beside them, so replacing either illustration means re-running
 * a command rather than redoing any of this.
 *
 * Brand palette, exact values:
 *   navy #07385e   orange #e3701e   maroon #87212e   gold #fec52d   grey #bcbec0
 */

const MAROON = '#87212e';

/**
 * A little yard sign planted next to him.
 *
 * Stamping his name and a "live" dot across the wall read as UI chrome bolted to
 * a cartoon. A sign staked into the ground belongs to the scene he's in — same
 * information, no seam between the character and the interface.
 */
export function YardSign({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 132 108" role="img" aria-label="Chat with Connor">
      {/* stakes, driven into the ground */}
      <path d="M40 62 v42 M92 62 v42" stroke="#7A5535" strokeWidth="6" strokeLinecap="round" />
      {/* the board, tilted a touch so it looks planted rather than pasted on */}
      <g transform="rotate(-3 66 40)">
        <rect x="6" y="8" width="120" height="62" rx="7" fill="#FDFCF9" stroke="#7A5535" strokeWidth="4" />
        <rect x="13" y="15" width="106" height="48" rx="4" fill="none" stroke={MAROON} strokeWidth="1.6" opacity=".35" />
        <text
          x="66"
          y="33"
          textAnchor="middle"
          fill={MAROON}
          style={{ font: "700 23px 'Caveat', 'Segoe Script', cursive" }}
        >
          Chat with
        </text>
        <text
          x="66"
          y="58"
          textAnchor="middle"
          fill={MAROON}
          style={{ font: "700 30px 'Caveat', 'Segoe Script', cursive" }}
        >
          Connor
        </text>
      </g>
    </svg>
  );
}

/**
 * Closed state: Connor over the wall — "I'm here if you need me."
 *
 * The wall is part of the illustration rather than drawn behind it, which is why
 * his hands grip the top edge convincingly.
 *
 * He blinks by cross-fading to a second frame with his eyes painted shut
 * (scripts/blink-peek.mjs). He's a raster now, so an animated SVG eyelid isn't
 * available, and a DOM overlay positioned over each eye would have to stay
 * aligned through every resize — it would drift the moment the artwork changed.
 * Two stacked frames cannot misalign.
 */
export function ConnorPeek({ className }: { className?: string }) {
  return (
    <span className={`connor-peek-art ${className ?? ''}`}>
      <img src="/connor-peek.png" alt="Connor" />
      <img className="is-blink" src="/connor-peek-blink.png" alt="" aria-hidden="true" />
    </span>
  );
}

/**
 * Open state: out from behind the wall, standing beside the chat.
 *
 * The AAS mark on his chest is painted into the raster by
 * `scripts/brand-connor.mjs`, not overlaid here. An overlay always read as a
 * sticker: the patch needed to hide the artwork's own generic "A" could never
 * match a shaded chest. With that A removed upstream there is nothing to hide.
 */
export function ConnorStanding({ className }: { className?: string }) {
  return (
    <div className={`connor-stand ${className ?? ''}`}>
      {/* Blinks the same way the peeking pose does — a second frame cross-faded
          over the first. One eye here rather than two, since he's in profile. */}
      <img className="connor-art" src="/connor.png" alt="Connor" />
      <img className="connor-art is-blink" src="/connor-blink.png" alt="" aria-hidden="true" />
      {/* Without a contact shadow he floats; with one he's standing on something. */}
      <span className="connor-shadow" aria-hidden="true" />
    </div>
  );
}
